import { Button, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import useDebounceValue from "hooks/useDebounceValue";
import {
  useInternalCompanyRecommendations,
  useSearchInternalCompanies,
} from "queries/internalCompany";

// "Pro · exp: 2 Jun 2025 · login: 1 Jun 2025 · ID: 10" (bagian kosong dilewati).
const companyMeta = (c) => {
  const parts = [];
  if (c.plan_name) parts.push(c.plan_name);
  if (c.expiry_date) {
    parts.push(`exp: ${dayjs(c.expiry_date).format("D MMM YYYY")}`);
  }
  if (c.last_login) {
    parts.push(`login: ${dayjs(c.last_login).format("D MMM YYYY")}`);
  }
  parts.push(`ID: ${c.id}`);
  return parts.join(" · ");
};

// Konten kartu perusahaan — dipakai identik di rekomendasi, kartu terpilih,
// dan opsi hasil pencarian (Nama, Paket, Exp, ID).
const CompanyContent = ({ c }) => (
  <span className="flex flex-col items-start">
    <span className="font-medium">{c.name}</span>
    <span className="text-xs opacity-70">{companyMeta(c)}</span>
  </span>
);

// Selektor perusahaan internal:
// - Ada rekomendasi → kartu yang bisa dipilih + tombol "Lainnya" (buka pencarian).
// - Tidak ada rekomendasi → langsung pencarian.
// Perusahaan terpilih dari pencarian ikut ditampilkan sebagai kartu identik.
const InternalCompanySelect = ({
  appId,
  contactId,
  contactLoading = false,
  selected,
  onChange,
}) => {
  const { t } = useTranslation();
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounceValue(search, 400);
  const canSearch = debouncedSearch.trim().length >= 2;
  const selectRef = useRef(null);

  // Fokuskan ke input pencarian saat mode "Lainnya" dibuka.
  useEffect(() => {
    if (showSearch) selectRef.current?.focus();
  }, [showSearch]);

  const recEnabled = appId != null && contactId != null;
  // isLoading (bukan isFetching) → data yang sudah tampil tak hilang saat refetch.
  const { data: recommendations = [], isLoading: loadingRec } =
    useInternalCompanyRecommendations(appId, contactId, {
      enabled: recEnabled,
    });

  const { data: searchResults = [], isFetching: loadingSearch } =
    useSearchInternalCompanies(appId, debouncedSearch.trim(), {
      enabled: appId != null && canSearch,
    });

  const hasRecommendations = recommendations.length > 0;
  // Tunggu kontak/rekomendasi siap dulu agar tak berkedip (search → memuat → box).
  const loading = contactLoading || (recEnabled && loadingRec);

  // Ganti app → daftar perusahaan beda, reset mode & keyword.
  useEffect(() => {
    setShowSearch(false);
    setSearch("");
  }, [appId]);

  const recIds = new Set(recommendations.map((r) => r.id));
  const selectedId = selected?.id ?? null;
  // Perusahaan terpilih yang bukan dari daftar rekomendasi (hasil "Lainnya").
  const showOtherCard = selected != null && !recIds.has(selectedId);

  const searchOptions = searchResults.map((c) => ({
    value: c.id,
    label: c.name,
    company: c,
  }));

  const optionRender = (option) => <CompanyContent c={option.data.company} />;

  // Mode pencarian: tanpa rekomendasi, atau user menekan "Lainnya".
  const searchMode = !hasRecommendations || showSearch;

  if (loading) {
    return (
      <p className="mb-0 text-xs text-neutral-400">
        {t("meeting_finished.loading_recommendations")}
      </p>
    );
  }

  const renderCard = (c, isSelected, onClick) => (
    <Button
      key={c.id}
      type={isSelected ? "primary" : "default"}
      className="h-auto w-full justify-start whitespace-normal py-1.5 text-left"
      onClick={onClick}
    >
      <CompanyContent c={c} />
    </Button>
  );

  return (
    <div className="space-y-2">
      {(hasRecommendations || showOtherCard) && (
        <div className="flex flex-col gap-2">
          {recommendations.map((c) =>
            renderCard(c, selectedId === c.id, () => {
              onChange?.(c);
              setShowSearch(false);
              setSearch("");
            })
          )}
          {showOtherCard &&
            // Kartu perusahaan "Lainnya" terpilih — klik untuk ganti via pencarian.
            renderCard(selected, true, () => {
              setSearch("");
              setShowSearch(true);
            })}
          {hasRecommendations && (
            <Button
              type={showSearch ? "primary" : "default"}
              className="w-full"
              // Buka pencarian + kosongkan pilihan sebelumnya, lalu fokus ke input.
              onClick={() => {
                onChange?.(null);
                setSearch("");
                setShowSearch(true);
              }}
            >
              {t("meeting_finished.company_other")}
            </Button>
          )}
        </div>
      )}

      {searchMode && (
        <Select
          ref={selectRef}
          showSearch
          className="w-full"
          placeholder={t("meeting_finished.select_company")}
          filterOption={false}
          onSearch={setSearch}
          searchValue={search}
          value={null}
          loading={loadingSearch}
          notFoundContent={
            loadingSearch
              ? null
              : canSearch
              ? t("no_data")
              : t("meeting_finished.type_to_search")
          }
          options={searchOptions}
          optionRender={optionRender}
          onChange={(_, option) => {
            onChange?.(option?.company ?? null);
            setSearch("");
            setShowSearch(false);
          }}
        />
      )}

      {hasRecommendations && showSearch && (
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => {
            setShowSearch(false);
            setSearch("");
          }}
        >
          {t("meeting_finished.back_to_recommendations")}
        </button>
      )}

      {!selected && (
        <p className="mb-0 text-xs text-red-500">
          {t("meeting_finished.select_company_required")}
        </p>
      )}
    </div>
  );
};

export default InternalCompanySelect;
